import type { Placement, ReferenceType, Strategy, UseFloatingReturn, UseInteractionsReturn } from '@floating-ui/react';
import {
  FloatingPortal,
  arrow,
  autoUpdate,
  limitShift,
  offset,
  safePolygon,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import { AnimatePresence } from 'motion/react';
import { Fragment, ReactNode, cloneElement, createContext, isValidElement, use, useMemo, useRef } from 'react';
import type { CSSProperties, HTMLProps, PropsWithChildren, ReactHTMLElement, Ref } from 'react';

import { Styles, css, sva } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { Token, token } from '@flipster/styled-system/tokens';

import { useBreakpoint } from '../../misc/responsive';
import { useControllableState } from '../../misc/state/use-controllable-state';
import * as Modal from '../modal/modal';
import { Arrow } from './arrow';

// background.* 또는 content.* 형태의 색상 토큰만 추출
type BackgroundColorToken = Extract<Token, `colors.background.${string}`>;
type ContentColorToken = Extract<Token, `colors.content.${string}`>;

export type TooltipOptions = UncontrolledTooltipOptions | ControlledTooltipOptions;
interface BaseTooltipOptions {
  strategy?: Strategy;
  placement?: Placement;
  maxWidth?: number;
  onOpenChange?: (open: boolean) => void;
}
interface UncontrolledTooltipOptions extends BaseTooltipOptions {
  open?: never;
}
interface ControlledTooltipOptions extends BaseTooltipOptions {
  open: boolean;
}

// 훅은 혹시 다른곳에서도 쓸수있기 때문에 controlled tooltip 코드를 남겨둠
interface UseTooltipReturn extends UseFloatingReturn<ReferenceType>, UseInteractionsReturn {
  arrowRef: Ref<HTMLDivElement | null>;
  open: boolean;
  setOpen: (open: boolean) => void;
}
export function useTooltip({
  strategy = 'absolute',
  placement = 'top',
  onOpenChange,
  maxWidth = 280,
  ...props
}: TooltipOptions): UseTooltipReturn {
  const { isMobile } = useBreakpoint();
  const arrowRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useControllableState({
    defaultValue: props.open ?? false,
    value: props.open,
    onChange: onOpenChange,
  });

  const data = useFloating({
    strategy,
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({ mainAxis: 12, crossAxis: 0 }),
      shift({
        padding: 8,
        limiter: limitShift({ offset: 16 }),
      }),
      size({
        apply({ elements, availableWidth }) {
          // oxlint-disable-next-line no-param-reassign
          elements.floating.style.maxWidth = `${Math.min(availableWidth, maxWidth)}px`;
        },
      }),
      arrow((state) => {
        const padding = Math.min(12, Math.min(state.rects.floating.width, state.rects.floating.height) / 2);
        return { element: arrowRef, padding };
      }),
    ],
  });

  const isUncontrolled = props.open == null;
  const { context } = data;

  const role = useRole(context, { role: 'tooltip' });
  const hover = useHover(context, {
    move: false,
    enabled: isUncontrolled && !isMobile,
    handleClose: safePolygon({ blockPointerEvents: true }),
  });
  const click = useClick(context, { enabled: isUncontrolled && isMobile });
  const dismiss = useDismiss(context, { enabled: !isMobile });
  const interactions = useInteractions([hover, click, dismiss, role]);

  return useMemo(
    () => ({
      open: open!,
      setOpen,
      ...interactions,
      ...data,
      arrowRef,
    }),
    [open, setOpen, interactions, data],
  );
}

interface TooltipContext extends ReturnType<typeof useTooltip> {}
const TooltipContext = createContext<TooltipContext>(null!);
export const useTooltipContext = () => {
  const context = use(TooltipContext);
  if (context == null) throw new Error('Tooltip components must be wrapped in <Tooltip />');
  return context;
};

const Root = ({ children, ...options }: PropsWithChildren<TooltipOptions>) => {
  const tooltip = useTooltip(options);
  return <TooltipContext value={tooltip}>{children}</TooltipContext>;
};

interface TooltipTriggerProps {
  ref?: Ref<HTMLElement>;
  asChild?: boolean;
  css?: Styles;
}
const Trigger = ({
  ref: propRef,
  children,
  asChild = false,
  css: cssProps,
}: PropsWithChildren<TooltipTriggerProps>) => {
  const context = useTooltipContext();
  const childrenRef = (children as ReactHTMLElement<HTMLElement>).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  if (asChild && isValidElement(children)) {
    return cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...(children.props as HTMLProps<Element>),
        // @ts-expect-error data bindings has no type definitions in HTMLProps
        'data-state': context.open ? 'open' : 'closed',
      }),
    );
  }

  return (
    <div
      ref={ref}
      // style the trigger based on the state
      data-state={context.open ? 'open' : 'closed'}
      className={css({ display: 'inline-flex' }, cssProps)}
      {...context.getReferenceProps()}
    >
      {children}
    </div>
  );
};

interface TooltipContentProps extends HTMLProps<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
  bottomSheetTitle?: ReactNode;
  portal?: true;
  colors?: { bgColor?: BackgroundColorToken; color?: ContentColorToken };
  /** 포털로 띄운 툴팁이 모달 등 더 높은 스태킹 위에 그려져야 할 때 z-index를 덮어쓴다. 예: token('zIndex.modal'). 기본값 token('zIndex.tooltip') */
  zIndex?: CSSProperties['zIndex'];
  css?: Styles;
  // Do not implement onClose prop. Use onOpenChange in Root instead.
  // onClose?(): void;
}
const Content = ({
  ref: propRef,
  bottomSheetTitle: title,
  css: cssProps,
  colors,
  children,
  portal,
  zIndex: zIndexOverride,
  ...props
}: TooltipContentProps) => {
  const { isMobile } = useBreakpoint();
  const context = useTooltipContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);
  const recipeCssRaw = tooltipRecipe.raw({ placement: context.placement });

  const Container = portal ? FloatingPortal : Fragment;
  const styles = {
    ...context.floatingStyles,
    '--text-color': token(colors?.color ?? `colors.content.default.level-5`),
    '--bg-color': token(colors?.bgColor ?? `colors.background.default.level-4`),
    zIndex: zIndexOverride ?? token('zIndex.tooltip'),
  };

  if (isMobile) {
    return (
      <AnimatePresence>
        {context.open && (
          <Modal.Root isOpen={context.open} mode='bottomSheet' onClose={() => context.setOpen(false)}>
            {title && (
              <Modal.Header>
                <Modal.Title label={title} />
              </Modal.Header>
            )}
            <Modal.Content>{children}</Modal.Content>
          </Modal.Root>
        )}
      </AnimatePresence>
    );
  }

  if (!context.open) return null;

  return (
    <Container {...(portal ? { id: 'modal' } : {})}>
      <div ref={ref} className={css(recipeCssRaw.tooltip)} style={styles} {...context.getFloatingProps(props)}>
        <div
          ref={context.arrowRef}
          className={css(recipeCssRaw.arrow)}
          style={{
            left: context.middlewareData.arrow?.x,
            top: context.middlewareData.arrow?.y,
          }}
        >
          <Arrow />
        </div>
        <div className={css(recipeCssRaw.content, cssProps)}>{children}</div>
      </div>
    </Container>
  );
};

Root.displayName = 'Tooltip.Root';
Trigger.displayName = 'Tooltip.Trigger';
Content.displayName = 'Tooltip.Content';
export { Root, Trigger, Content };

export const placements = [
  'top',
  'right',
  'bottom',
  'left',
  'top-start',
  'top-end',
  'bottom-start',
  'bottom-end',
  'left-start',
  'left-end',
  'right-start',
  'right-end',
] as const;

type PlacementTypes = (typeof placements)[number];

const tooltipRecipe = sva({
  slots: ['tooltip', 'content', 'arrow'],
  base: {
    tooltip: {
      ...hstack.raw({
        textStyle: 'Text/M/Regular',
        alignItems: 'flex-start',
        py: 10,
        px: 12,
        width: 'max-content',
        rounded: 8,
        bgColor: 'var(--bg-color)',
        color: 'var(--text-color)',
      }),
    },
    arrow: {
      pos: 'absolute',
      pointerEvents: 'none',
      lineHeight: 0,
    },
  },
  variants: {
    placement: placements.reduce((acc, cur) => ({ ...acc, [cur]: {} }), {} as Record<PlacementTypes, {}>),
  },
  compoundVariants: [
    {
      placement: ['top', 'top-start', 'top-end'],
      css: {
        arrow: {
          top: '100%',
          left: '50%',
        },
      },
    },
    {
      placement: ['right', 'right-start', 'right-end'],
      css: {
        arrow: {
          top: '50%',
          right: 'calc(100% - 3px)',
          rotate: '90deg',
        },
      },
    },
    {
      placement: ['bottom', 'bottom-start', 'bottom-end'],
      css: {
        arrow: {
          bottom: '100%',
          left: '50%',
          rotate: '180deg',
        },
      },
    },
    {
      placement: ['left', 'left-start', 'left-end'],
      css: {
        arrow: {
          top: '50%',
          left: 'calc(100% - 3px)',
          rotate: '-90deg',
        },
      },
    },
  ],
});
